import type { FieldMachineOption } from "@/api/fieldLeads";
import { FIELD_LEAD_DRAFT_ID, readDraft, removeDraft, writeDraft } from "@/offline/db";
import { closeDraftDb, withDraftDb } from "@/offline/draftDb";
import type { FieldLeadContactSource } from "./historyDb";

export interface AgendaContact {
  name: string;
  phone: string;
}

export interface FieldLeadDraft {
  clientLoadId: string;
  source: FieldLeadContactSource;
  agendaContact: AgendaContact | null;
  name: string;
  phone: string;
  machine: FieldMachineOption | null;
  comment: string;
}

export function emptyFieldLeadDraft(
  clientLoadId: string,
  source: FieldLeadContactSource,
): FieldLeadDraft {
  return {
    clientLoadId,
    source,
    agendaContact: null,
    name: "",
    phone: "",
    machine: null,
    comment: "",
  };
}

export function isEmptyFieldLeadDraft(draft: FieldLeadDraft): boolean {
  return (
    draft.agendaContact === null &&
    draft.name.trim() === "" &&
    draft.phone.trim() === "" &&
    draft.machine === null &&
    draft.comment.trim() === ""
  );
}

export function asManualDraft(draft: FieldLeadDraft): FieldLeadDraft {
  return {
    ...draft,
    source: "manual",
    name: draft.agendaContact?.name ?? draft.name,
    phone: draft.agendaContact?.phone ?? draft.phone,
    agendaContact: null,
  };
}

export const closeFieldLeadDraftStore = closeDraftDb;

export function loadFieldLeadDraft(ownerKey: string): Promise<FieldLeadDraft | null> {
  return withDraftDb(async (opened) => {
    const stored = await readDraft<FieldLeadDraft>(opened, FIELD_LEAD_DRAFT_ID);
    if (stored === null) {
      return null;
    }
    if (stored.ownerKey !== ownerKey) {
      await removeDraft(opened, FIELD_LEAD_DRAFT_ID);
      return null;
    }
    return stored.state;
  }).catch(() => null);
}

export const DRAFT_READ_TIMEOUT_MS = 3_000;

export function nullAfterTimeout<T>(
  work: Promise<T | null>,
  timeoutMs: number = DRAFT_READ_TIMEOUT_MS,
): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  return Promise.race([work.catch(() => null), expired]).finally(() => clearTimeout(timer));
}

export function saveFieldLeadDraft(ownerKey: string, draft: FieldLeadDraft): Promise<void> {
  return withDraftDb(async (opened) => {
    await writeDraft(opened, {
      id: FIELD_LEAD_DRAFT_ID,
      ownerKey,
      state: draft,
      updatedAt: new Date().toISOString(),
    });
  }).catch(() => undefined);
}

export function clearFieldLeadDraft(clientLoadId: string): Promise<void> {
  return withDraftDb(async (opened) => {
    const stored = await readDraft<FieldLeadDraft>(opened, FIELD_LEAD_DRAFT_ID);
    if (stored?.state.clientLoadId === clientLoadId) {
      await removeDraft(opened, FIELD_LEAD_DRAFT_ID);
    }
  }).catch(() => undefined);
}
